// OnboardingSlideshow — AI-narrated slideshow embedded in the User Guide.
// Fetches slide manifest, triggers backend TTS generation on first mount (idempotent),
// and plays MP3 audio synced with visual slides. Auto-advances at track end.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Pause, SkipBack, SkipForward, RotateCcw, Volume2, Sparkles, Loader2 } from 'lucide-react';
import { API } from '../utils/api';

const OnboardingSlideshow = () => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);        // audio generated & manifest fetched
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);      // 0-100 for current clip
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Load manifest + kick off (idempotent) generation on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await axios.get(`${API}/onboarding/slides`);
        if (cancelled) return;
        setSlides(m.data.slides || []);
        setGenerating(true);
        const g = await axios.post(`${API}/onboarding/generate`);
        if (cancelled) return;
        if (g.data?.ok) {
          setReady(true);
        } else {
          toast.error('Narration generation returned an unexpected response.');
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to prepare narration.');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentSlide = slides[idx];

  const advance = useCallback(() => {
    setPlaying(false);
    if (idx < slides.length - 1) {
      setIdx(idx + 1);
      // Auto-continue playback on the next slide
      setTimeout(() => setPlaying(true), 250);
    }
  }, [idx, slides.length]);

  // Attach audio-element event handlers whenever idx changes.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => advance();
    const onTime = () => {
      if (audio.duration > 0) setProgress((audio.currentTime / audio.duration) * 100);
    };
    const onLoaded = () => setDuration(audio.duration || 0);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [idx, advance]);

  // React to play/pause state.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  }, [playing, idx]);

  const togglePlay = () => {
    if (!ready) return;
    setPlaying(p => !p);
  };
  const restart = () => {
    setIdx(0);
    setProgress(0);
    setTimeout(() => setPlaying(true), 100);
  };
  const jumpTo = (i) => {
    setIdx(i);
    setProgress(0);
    setTimeout(() => setPlaying(true), 100);
  };

  if (!slides.length) {
    return (
      <div className="form-card" style={{ padding: 40, textAlign: 'center' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ margin: '0 auto', color: 'var(--navy)' }} />
        <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>Loading walkthrough...</div>
      </div>
    );
  }

  const audioUrl = `${API}/onboarding/audio/${currentSlide.id}.mp3`;

  return (
    <div className="form-card" data-testid="onboarding-slideshow" style={{ marginBottom: 24, overflow: 'hidden' }}>
      {/* Header ribbon */}
      <div style={{
        padding: '12px 20px', background: 'linear-gradient(90deg, var(--navy) 0%, var(--navy-light) 100%)',
        color: '#fff', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Sparkles className="w-4 h-4" style={{ color: 'var(--gold)' }} />
        <div style={{ fontSize: 13, fontWeight: 500, letterSpacing: '.3px' }}>
          Walkthrough {generating && '(preparing audio...)'}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.75 }}>{idx + 1} / {slides.length}</div>
      </div>

      {/* Main slide area */}
      <div style={{
        minHeight: 220, padding: '32px 28px',
        background: currentSlide.accent, color: '#fff',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        transition: 'background 0.35s ease',
      }}>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, fontWeight: 600, marginBottom: 12, textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
          {currentSlide.title}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.7, opacity: 0.95, maxWidth: 640 }}>
          {currentSlide.narration}
        </div>
      </div>

      {/* Controls */}
      <div style={{ padding: '14px 20px', background: '#fafafa', borderTop: '1px solid #eef1f5' }}>
        {/* Progress bar */}
        <div style={{ width: '100%', height: 4, background: '#e5e7eb', borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'var(--gold)', transition: 'width 0.2s linear' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={restart} title="Restart" data-testid="slideshow-restart"
                  style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 6, padding: 8, cursor: 'pointer' }}>
            <RotateCcw className="w-4 h-4" style={{ color: '#666' }} />
          </button>
          <button onClick={() => idx > 0 && jumpTo(idx - 1)} disabled={idx === 0} title="Previous"
                  data-testid="slideshow-prev"
                  style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 6, padding: 8, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>
            <SkipBack className="w-4 h-4" style={{ color: '#666' }} />
          </button>
          <button onClick={togglePlay} disabled={!ready} data-testid="slideshow-play"
                  style={{
                    background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: '50%',
                    width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.5,
                  }}>
            {!ready
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : (playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" style={{ marginLeft: 2 }} />)}
          </button>
          <button onClick={() => idx < slides.length - 1 && jumpTo(idx + 1)} disabled={idx === slides.length - 1} title="Next"
                  data-testid="slideshow-next"
                  style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 6, padding: 8, cursor: idx === slides.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === slides.length - 1 ? 0.4 : 1 }}>
            <SkipForward className="w-4 h-4" style={{ color: '#666' }} />
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888' }}>
            <Volume2 className="w-3 h-3" />
            {duration ? `${Math.floor(duration)}s` : '—'}
          </div>
        </div>

        {/* Slide dots */}
        <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {slides.map((s, i) => (
            <button key={s.id} onClick={() => jumpTo(i)} title={s.title}
                    aria-label={`Jump to ${s.title}`}
                    style={{
                      width: 8, height: 8, borderRadius: '50%', padding: 0, border: 'none',
                      background: i === idx ? 'var(--navy)' : '#d1d5db', cursor: 'pointer',
                      transition: 'background 0.2s',
                    }} />
          ))}
        </div>
      </div>

      {/* Hidden audio element — key forces reload on slide change */}
      {ready && (
        <audio ref={audioRef} key={currentSlide.id} src={audioUrl} preload="auto" />
      )}
    </div>
  );
};

export default OnboardingSlideshow;
